// src/pages/ResetPassword.jsx

import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/api";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // ✅ Import the eye icons
import { useTranslation } from "react-i18next";

const ResetPassword = () => {
  const { t } = useTranslation();
  // Initialize hooks
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  }); // ✅ New state for password visibility

  // ✅ New handler to toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPassword((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (newPassword !== confirmPassword) {
      setMessage(t("auth.resetPassword.passwordsDoNotMatch"));
      return;
    }

    if (newPassword.length < 8) {
      setMessage(t("auth.common.passwordMinLength"));
      return;
    }

    if (!token) {
      setMessage(t("auth.resetPassword.missingToken"));
      return;
    }

    try {
      const response = await api.post("/auth/reset-password", {
        token,
        newPassword,
      });

      setMessage(response.data.message);
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      console.error("Reset password error:", error);
      setMessage(error.response?.data?.message || t("common.errorTryAgain"));
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          {t("auth.resetPassword.title")}
        </h2>

        {message && (
          <p
            className={`mt-4 text-center ${
              message.includes("success") ? "text-green-500" : "text-red-500"
            }`}
          >
            {message}
          </p>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="newPassword"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              {t("auth.resetPassword.newPassword")}
            </label>
            <div className="relative">
              <input
                type={showPassword.new ? "text" : "password"} // ✅ Dynamic type
                id="newPassword"
                name="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <span
                onClick={() => togglePasswordVisibility("new")} // ✅ Toggle on click
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500 hover:text-gray-700"
              >
                {showPassword.new ? <FaEyeSlash /> : <FaEye />}{" "}
                {/* ✅ Render icon based on state */}
              </span>
            </div>
          </div>

          <div className="mb-6">
            <label
              htmlFor="confirmPassword"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              {t("auth.resetPassword.confirmPassword")}
            </label>
            <div className="relative">
              <input
                type={showPassword.confirm ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <span
                onClick={() => togglePasswordVisibility("confirm")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500 hover:text-gray-700"
              >
                {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-blue-500 text-white font-bold py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:shadow-outline"
          >
            {t("auth.resetPassword.submit")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
